import InputField from "components/fields/InputField";
import TextField from "components/fields/TextField";
import { getAtPath } from "utils/cms";
import ImageField from "./ImageField";

export function FrHint({ text }) {
  if (!text) return null;
  return <p className="mt-0.5 text-[11px] text-gray-500">FR: {text}</p>;
}

export function GroupBox({ title, children, cols, plain = false }) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        plain
          ? "border-gray-200 bg-white dark:border-white/10 dark:bg-navy-800"
          : "border-purple-100 bg-[#F7F5FF] dark:border-white/10 dark:bg-white/5"
      }`}
    >
      {title ? (
        <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A4CF0]">
          {title}
        </p>
      ) : null}
      <div
        className={
          cols
            ? `grid grid-cols-1 gap-2.5 ${cols}`
            : "flex flex-col gap-2.5"
        }
      >
        {children}
      </div>
    </div>
  );
}

export function FieldRow({ children }) {
  return (
    <div className="grid max-w-3xl grid-cols-1 gap-2.5 sm:grid-cols-2">
      {children}
    </div>
  );
}

export function CardRow({ children, cols = "md:grid-cols-2" }) {
  return (
    <div className={`grid grid-cols-1 gap-3 ${cols}`}>{children}</div>
  );
}

export function TextInput({
  label,
  path,
  form,
  setField,
  frMap,
  extra,
  type = "text",
  placeholder,
}) {
  return (
    <div className={extra}>
      <InputField
        label={label}
        id={path}
        type={type}
        placeholder={placeholder}
        value={getAtPath(form, path) ?? ""}
        onChange={(event) => setField(path, event.target.value)}
      />
      <FrHint text={frMap?.[path]} />
    </div>
  );
}

export function AreaInput({
  label,
  path,
  form,
  setField,
  frMap,
  extra,
  rows = 3,
}) {
  return (
    <div className={extra || ""}>
      <TextField
        label={label}
        id={path}
        rows={rows}
        value={getAtPath(form, path) ?? ""}
        onChange={(event) => setField(path, event.target.value)}
      />
      <FrHint text={frMap?.[path]} />
    </div>
  );
}

export function FileInput({ label, path, form, setField, extra = "max-w-sm" }) {
  return (
    <div className={extra}>
      <ImageField
        id={path}
        label={label}
        value={getAtPath(form, path) ?? ""}
        onChange={(value) => setField(path, value)}
      />
    </div>
  );
}

export function LogoList({ form, setField, path = "logos" }) {
  const logos = getAtPath(form, path) || [];
  return (
    <div className="space-y-2.5">
      {logos.map((_, index) => (
        <div
          key={`${path}-${index}`}
          className="rounded-lg border border-purple-100 bg-white p-2.5 dark:border-white/10 dark:bg-white/5"
        >
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A4CF0]">
            Logo {index + 1}
          </p>
          <FileInput
            label="Image"
            path={`${path}.${index}`}
            form={form}
            setField={setField}
          />
        </div>
      ))}
    </div>
  );
}

export function StringList({
  label,
  items,
  onChange,
  inline = false,
}) {
  const list = items || [];
  const cols =
    list.length <= 1
      ? "grid-cols-1"
      : list.length === 2
        ? "grid-cols-2"
        : list.length === 3
          ? "grid-cols-3"
          : "grid-cols-4";

  return (
    <div className={inline ? "" : "max-w-xl space-y-2"}>
      <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.14em] text-[#6A4CF0]">
        {label}
      </p>
      <div className={inline ? `grid ${cols} gap-2` : "space-y-2"}>
        {list.map((item, index) => (
          <InputField
            key={`${label}-${index}`}
            label={inline ? "" : `${label} ${index + 1}`}
            id={`${label}-${index}`}
            placeholder={`${label} ${index + 1}`}
            value={item}
            onChange={(event) => {
              const next = [...list];
              next[index] = event.target.value;
              onChange(next);
            }}
          />
        ))}
      </div>
    </div>
  );
}
